import React, { useEffect, useRef } from 'react'
import type { ActionRecord } from '@range-reader/shared'
import { PlayerTooltip } from '../players/PlayerTooltip'

const ACTION_COLORS: Record<string, string> = {
  fold:  '#9ca3af',
  check: '#e5e7eb',
  call:  '#93c5fd',
  bet:   '#86efac',
  raise: '#fde047',
  allin: '#fca5a5',
}

const STREET_COLORS: Record<string, string> = {
  preflop: '#6366f1',
  flop:    '#10b981',
  turn:    '#f59e0b',
  river:   '#ef4444',
}

const STREET_LABELS: Record<string, string> = {
  preflop: 'PREFLOP',
  flop:    'FLOP',
  turn:    'TURN',
  river:   'RIVER',
}

interface ActionLogProps {
  actions: ActionRecord[]
  currentIndex: number
  isAnonymized: boolean
  bigBlind?: number           // in cents
  playerNameMap?: Record<string, string>
}

export function ActionLog({ actions, currentIndex, isAnonymized, bigBlind = 0, playerNameMap = {} }: ActionLogProps) {
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [currentIndex])

  const visibleActions = actions.slice(0, currentIndex + 1)
  const bb = bigBlind > 0 ? bigBlind / 100 : 1

  // Track pot for % pot display
  let runningPot = bigBlind * 1.5 // SB + BB

  let lastStreet = ''

  return (
    <div className="flex-1 overflow-y-auto text-xs space-y-0.5 pr-1">
      {visibleActions.map((action, i) => {
        const streetHeader = action.street !== lastStreet
        if (action.street) lastStreet = action.street

        const displayName = playerNameMap[action.player] ?? action.player
        const streetColor = STREET_COLORS[action.street] ?? '#6b7280'

        // Format amount in BB with % pot
        let amountStr = ''
        if (action.amount > 0) {
          const bbs = action.amount / 100 / bb
          const bbFormatted = bbs % 1 === 0 ? `${bbs}` : bbs.toFixed(1)
          const potPct = runningPot > 0 ? Math.round((action.amount / runningPot) * 100) : 0
          amountStr = ` ${bbFormatted}bb`
          if (potPct > 0 && ['bet', 'raise', 'allin'].includes(action.action)) {
            amountStr += ` (${potPct}%)`
          }
        }

        // Update running pot after this action
        if (['bet', 'raise', 'call', 'allin'].includes(action.action)) {
          runningPot += action.amount
        }

        const color = ACTION_COLORS[action.action] ?? '#d1d5db'
        const isCurrent = i === currentIndex

        return (
          <React.Fragment key={i}>
            {streetHeader && action.street && (
              <div style={{
                color: streetColor, fontSize: 10, padding: '3px 0',
                borderTop: '1px solid #374151', marginTop: 4,
                fontWeight: 700, letterSpacing: '0.05em',
              }}>
                ── {STREET_LABELS[action.street] ?? action.street.toUpperCase()} ──
              </div>
            )}
            <div style={{
              padding: '2px 4px', borderRadius: 3,
              background: isCurrent ? '#374151' : 'transparent',
              borderLeft: `3px solid ${streetColor}`,
              marginLeft: 2,
            }}>
              <PlayerTooltip name={action.player}>
                <span style={{ color: '#d1d5db', fontWeight: 600 }}>{displayName}</span>
              </PlayerTooltip>
              {' '}
              <span style={{ color, fontWeight: action.action === 'allin' ? 700 : 400 }}>
                {action.action}{amountStr}
              </span>
            </div>
          </React.Fragment>
        )
      })}
      <div ref={bottomRef} />
    </div>
  )
}
